import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { buildValueToLabel, groupTokens } from "./tacticModel";
import { ZoneThumbnail } from "./ZoneThumbnail";
import "./TacticEditor.css";

// CALL_TACTIC action wire value — must match tokens.hh / tacticModel.js.
const CALL_ACTION = 1030;

// value → param type ("zone", …) for the actions that take an argument.
// Mirrors the query-side helper in QueryBuilderModal (kept local so the two
// token families stay independent).
const buildParamByValue = (actions) =>
  Object.fromEntries(
    Object.entries(actions)
      .filter(([, tk]) => typeof tk === "object" && tk.param)
      .map(([, tk]) => [tk.value, tk.param])
  );

// A grouped, collapsible list of tokens (actions or zones). renderIcon(value)
// is the optional per-option icon hook (zones pass a mini-field thumbnail).
function GroupedList({ tokenMap, currentValue, onPick, renderIcon, extra }) {
  const groups = groupTokens(tokenMap);
  const currentGroup = Object.entries(groups).find(([, items]) =>
    items.some(({ value }) => value === currentValue)
  )?.[0];
  const [openGroup, setOpenGroup] = useState(currentGroup ?? Object.keys(groups)[0]);

  return (
    <div className="action-picker">
      {Object.entries(groups).map(([group, items]) => (
        <div key={group} className="action-picker-group">
          <button
            className={`action-picker-group-label ${openGroup === group ? "open" : ""}`}
            onClick={() => setOpenGroup(openGroup === group ? null : group)}
          >
            <span>{group}</span>
            <span className="action-picker-arrow">{openGroup === group ? "▾" : "▸"}</span>
          </button>
          {openGroup === group && items.map(({ label, value }) => (
            <button
              key={value}
              className={`action-picker-option ${renderIcon ? "action-picker-option--icon" : ""} ${value === currentValue ? "selected" : ""}`}
              onClick={() => onPick(value)}
            >
              {renderIcon && renderIcon(value)}
              <span>{label}</span>
            </button>
          ))}
        </div>
      ))}
      {extra}
    </div>
  );
}

// A collapsible group whose options are the user's CALLABLE tactics (the ones
// not assigned to any player). Picking one makes the statement call it
// (@name). Shows <empty> when there are none. See Docs/tactic-calls.txt.
function CallGroup({ callables, onPickTactic }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const list = callables ?? [];
  return (
    <div className="action-picker-group">
      <button
        className={`action-picker-group-label ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{t("tactic.callGroup")}</span>
        <span className="action-picker-arrow">{open ? "▾" : "▸"}</span>
      </button>
      {open && list.length === 0 && (
        <div className="action-picker-option" style={{ opacity: 0.5, cursor: "default" }}>
          &lt;empty&gt;
        </div>
      )}
      {open && list.map((c) => (
        <button
          key={c.id}
          className="action-picker-option"
          onClick={() => onPickTactic(c.id)}
        >
          <span>{c.name}</span>
        </button>
      ))}
    </div>
  );
}

const renderZoneIcon = (value) => <ZoneThumbnail value={value} size={26} />;

/**
 * Grouped dropdown to pick an action. Two-step for parametrized (zone) actions.
 * A trailing "Call tactic" group lists the callable tactics. onSelect contract:
 *   onSelect(actionValue, zoneArg|null, callTarget|null)
 */
function ActionPicker({ actions, zones, callables, currentValue, onSelect }) {
  const [pendingAction, setPendingAction] = useState(null);
  const paramByValue = buildParamByValue(actions);

  if (pendingAction != null) {
    return (
      <GroupedList
        tokenMap={zones ?? {}}
        renderIcon={renderZoneIcon}
        onPick={(zoneVal) => onSelect(pendingAction, zoneVal, null)}
      />
    );
  }
  return (
    <GroupedList
      tokenMap={actions}
      currentValue={currentValue}
      onPick={(aVal) => {
        if (paramByValue[aVal]) setPendingAction(aVal); // -> zone step
        else onSelect(aVal, null, null);
      }}
      extra={<CallGroup callables={callables} onPickTactic={(id) => onSelect(CALL_ACTION, null, id)} />}
    />
  );
}

/**
 * An action token chip that opens a grouped picker on click. A parametrized
 * action shows its zone argument; a CALL shows @calledTacticName.
 * @param {{ value: number, arg?: number|null, callTarget?: number|null, actions: object, zones?: object, callables?: {id:number,name:string}[], onSelect: (value: number, arg: number|null, callTarget: number|null) => void }} props
 */
export function ActionChip({ value, arg = null, callTarget = null, actions, zones, callables, onSelect }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const isCall = value === CALL_ACTION;
  const calleeName = isCall
    ? ((callables ?? []).find((c) => c.id === callTarget)?.name ?? callTarget)
    : null;
  const label = isCall ? `${t("tactic.callVerb")} ${calleeName}` : (buildValueToLabel(actions)[value] ?? value);
  const zoneLabel = (!isCall && arg != null) ? (buildValueToLabel(zones ?? {})[arg] ?? arg) : null;

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="action-chip-wrapper">
      <span
        className="action-chip selectable"
        onClick={() => setOpen((o) => !o)}
        title="Tap to change action"
      >
        {label}
        {zoneLabel != null && <span className="action-chip-arg">({zoneLabel})</span>}
      </span>
      {open && (
        <ActionPicker
          actions={actions}
          zones={zones}
          callables={callables}
          currentValue={value}
          onSelect={(v, a, ct) => { onSelect(v, a, ct); setOpen(false); }}
        />
      )}
    </div>
  );
}
